import { Component } from '@theme/component';
import { CartErrorEvent, CartLinesUpdateEvent, ProductSelectEvent } from '@shopify/events';
import { morphSection, sectionRenderer } from '@theme/section-renderer';

/** Tempo massimo per l'aggiunta al carrello, in millisecondi. */
const TEMPO_MASSIMO = 12000;
/** Quanto aspettare al massimo le foto delle righe del cassetto prima di aprirlo. */
const ATTESA_FOTO = 450;
/** Sezione del cassetto carrello (snippet cart-drawer, sezione cart-drawer-section). */
const SEZIONE_CASSETTO = 'cart-drawer-section';

/**
 * @typedef {Object} Variante
 * @property {number} id
 * @property {string} titolo
 * @property {string[]} opzioni
 * @property {number} prezzo
 * @property {number} pieno
 * @property {boolean} disponibile
 * @property {number | null} media
 * @property {string} sku
 */

/**
 * @typedef {Object} DatiOfferta
 * @property {{ id: number, titolo: string, handle: string, url: string }} prodotto
 * @property {string[]} opzioni
 * @property {Variante[]} varianti
 * @property {number | null} mediaIniziale
 * @property {string} formatoPrezzo
 * @property {string} valuta
 * @property {string} urlAggiungi
 * @property {string} urlCarrello
 * @property {string} urlCarrelloJs
 * @property {{ testo: string, min: number, max: number }} consegna
 * @property {Record<string, string>} testi
 */

/**
 * @typedef {Object} LdyOffertaRefs
 * @property {HTMLScriptElement} dati - JSON con prodotto, varianti e testi.
 * @property {HTMLFormElement} form - Form vero verso /cart/add (funziona anche senza JavaScript).
 * @property {HTMLButtonElement} bottone - Bottone «Aggiungi al carrello».
 * @property {HTMLElement} testoBottone - Testo dentro il bottone.
 * @property {HTMLElement} errore - Zona dei messaggi d'errore (aria-live).
 * @property {HTMLInputElement[]} [scelte] - Radio delle schede (1 paio, 2+1).
 * @property {HTMLElement} [righe] - Contenitore delle righe colore/taglia, spostato nella scheda scelta.
 * @property {HTMLSelectElement | HTMLInputElement} [campoId] - Campo `id` usato dal form senza JavaScript.
 * @property {HTMLElement} [prezzo1]
 * @property {HTMLElement} [pieno1]
 * @property {HTMLElement} [pieno1wrap]
 * @property {HTMLElement} [sconto1]
 * @property {HTMLElement} [prezzo3]
 * @property {HTMLElement} [pieno3]
 * @property {HTMLElement} [pieno3wrap]
 * @property {HTMLElement} [sconto3]
 * @property {HTMLElement} [risparmio] - Importo della pillola «risparmi €72,00» (barrato meno prezzo del 2+1).
 * @property {HTMLElement} [consegna]
 * @property {HTMLElement} [consegnaTesto]
 */

/** Errore dell'aggiunta al carrello con il tipo che decide il messaggio. */
class ErroreCarrello extends Error {
  /**
   * @param {'disponibilita' | 'generico'} tipo
   * @param {string} [dettaglio] - Testo già leggibile restituito da Shopify.
   */
  constructor(tipo, dettaglio = '') {
    super(dettaglio || tipo);
    this.name = 'ErroreCarrello';
    this.tipo = tipo;
    this.dettaglio = dettaglio;
  }
}

/**
 * Selettore offerta della scheda prodotto: 1 paio o 2+1, colore e taglia per ogni paio,
 * una sola richiesta a /cart/add.js e cassetto aggiornato e aperto con gli eventi del tema.
 *
 * @extends {Component<LdyOffertaRefs>}
 */
export class LdyOffertaComponent extends Component {
  requiredRefs = ['dati', 'form', 'bottone', 'testoBottone', 'errore'];

  /** @type {DatiOfferta | null} */
  #dati = null;
  #occupato = false;
  #testoBottone = '';
  /** @type {number | null} */
  #mediaMostrata = null;
  /** @type {AbortController | null} */
  #richiestaGalleria = null;
  /** @type {Promise<typeof import('@theme/money-formatting')> | null} */
  #moduloPrezzi = null;
  #fotogrammaErrore = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#inizializza();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#inizializza();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    cancelAnimationFrame(this.#fotogrammaErrore);
    this.#richiestaGalleria?.abort();
    this.#richiestaGalleria = null;
  }

  /**
   * Scelta della scheda (radio `quantity`): sposta le righe nella scheda scelta e mostra 1 o 3 righe.
   * @param {Event} event - Evento `change` del radio.
   */
  scegli(event) {
    const radio = event.target;
    if (!(radio instanceof HTMLInputElement) || !radio.checked) return;

    const quantita = Number(radio.value) || 1;
    this.dataset.quantita = String(quantita);

    const { righe } = this.refs;
    // Schede del modo classico (.ldy-offerta__scheda) o del modo «pacchi» ([data-scheda], 25/10/2026).
    const slot = radio.closest('.ldy-offerta__scheda, [data-scheda]')?.querySelector('.ldy-offerta__slot');
    if (righe instanceof HTMLElement && slot instanceof HTMLElement && righe.parentElement !== slot) {
      slot.append(righe);
    }

    for (const riga of this.#righe()) {
      riga.hidden = Number(riga.dataset.riga) > quantita;
    }

    if (quantita > 1) this.#copiaPrimaRiga();

    this.#pulisciErrore();
    this.#aggiornaPrezzi();
  }

  /**
   * Cambio di colore o taglia in una riga. La riga 1 comanda le righe non toccate a mano e la galleria.
   * @param {Event} event - Evento `change` della tendina.
   */
  cambiaOpzione(event) {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;

    const riga = select.closest('[data-riga]');
    if (!(riga instanceof HTMLElement)) return;

    const numero = Number(riga.dataset.riga);
    if (numero > 1) riga.dataset.modificata = 'true';

    this.#pulisciErrore();
    this.#aggiornaRiga(riga);

    if (numero === 1) {
      this.#copiaPrimaRiga();
      this.#sincronizzaCampoId();
      this.#aggiornaGalleria();
    }

    this.#aggiornaPrezzi();
  }

  /**
   * Invio del form: una sola richiesta a /cart/add.js con tutte le righe, poi cassetto aggiornato e aperto.
   * @param {SubmitEvent} event - Evento `submit` del form.
   */
  async aggiungi(event) {
    const dati = this.#dati;
    // Senza dati leggibili lascia lavorare il form vero (va su /cart).
    if (!dati) return;

    event.preventDefault();
    if (this.#occupato || this.refs.bottone.disabled) return;

    const pezzi = this.#pezziDaAggiungere();
    if (!pezzi) return;

    this.#impostaOccupato(true);
    this.#pulisciErrore();

    const items = [...pezzi].map(([id, quantity]) => ({ id, quantity }));

    // 26/10/2026: carrello a tendina nuovo (assets/ldy-carrello-tendina.js, impianto di BabyWelt). Il pannello si
    // apre subito, una richiesta sola porta indietro il carrello ridisegnato; gli errori di Shopify li mostra lui.
    const carrelloNuovo = /** @type {any} */ (window).ldyCarrello;
    if (carrelloNuovo && typeof carrelloNuovo.aggiungi === 'function') {
      try {
        await carrelloNuovo.aggiungi(items);
      } finally {
        this.#impostaOccupato(false);
      }
      return;
    }

    const sezioni = this.#sezioniCarrello();
    /** @type {Record<string, unknown>} */
    const corpo = { items };
    if (sezioni.length) {
      corpo.sections = sezioni.join(',');
      corpo.sections_url = window.location.pathname;
    }

    const controller = new AbortController();
    let scaduto = false;
    const timer = setTimeout(() => {
      scaduto = true;
      controller.abort();
    }, TEMPO_MASSIMO);

    try {
      const risposta = await fetch(dati.urlAggiungi, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(corpo),
        signal: controller.signal,
      });
      const json = await risposta.json().catch(() => null);
      clearTimeout(timer);

      if (!risposta.ok || !json || json.status) {
        const dettaglio = typeof json?.description === 'string' ? json.description : '';
        throw new ErroreCarrello(risposta.status === 422 ? 'disponibilita' : 'generico', dettaglio);
      }

      /** @type {Record<string, string> | null} */
      const sezioniHtml = json.sections && typeof json.sections === 'object' ? json.sections : null;
      let carrello = this.#carrelloDaHtml(sezioniHtml?.[SEZIONE_CASSETTO]);
      if (sezioniHtml) await this.#riempiCassetto(sezioniHtml[SEZIONE_CASSETTO]);
      carrello ??= await this.#leggiCarrello();

      const differito = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
          promise: differito.promise,
        })
      );
      differito.resolve({
        cart: carrello,
        detail: {
          sections: sezioniHtml ?? undefined,
          itemCount: carrello?.totalQuantity ?? items.reduce((somma, item) => somma + item.quantity, 0),
          source: 'ldy-offerta',
          productId: String(dati.prodotto.id),
          didError: false,
        },
      });

      this.#impostaOccupato(false);
      this.#apriCassetto();
    } catch (errore) {
      clearTimeout(timer);
      this.#gestisciErrore(errore, scaduto);
    }
  }

  /**
   * Prepara dati e stato visibile. Idempotente: gira a ogni collegamento e dopo i re-render.
   */
  #inizializza() {
    try {
      this.#dati = JSON.parse(this.refs.dati.textContent || 'null');
    } catch (errore) {
      this.#dati = null;
      console.warn('[ldy-offerta] Dati non leggibili, resta il form normale.', errore);
      return;
    }
    if (!this.#dati) return;

    // shop.money_format può contenere entità HTML (es. «&euro;{{amount}}»): dentro lo <script> JSON restano scritte così.
    this.#dati.formatoPrezzo = decodificaEntita(this.#dati.formatoPrezzo ?? '');
    this.#mediaMostrata = this.#dati.mediaIniziale ?? null;
    if (!this.#occupato) this.#testoBottone = this.refs.testoBottone.textContent?.trim() ?? '';

    for (const riga of this.#righe()) this.#aggiornaRiga(riga);
    this.#sincronizzaCampoId();
    this.#aggiornaPrezzi();
    this.#mostraConsegna();
  }

  /** @returns {HTMLElement[]} Le tre righe in ordine. */
  #righe() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-riga]'))).sort(
      (a, b) => Number(a.dataset.riga) - Number(b.dataset.riga)
    );
  }

  /** @returns {number} Le paia della scheda scelta: 1 o 3 nel modo classico, 2 / 3 / 4 nel modo «pacchi». */
  #quantita() {
    const scelta = this.refs.scelte?.find((radio) => radio.checked);
    return Number(scelta?.value) || 1;
  }

  /**
   * Valori scelti in una riga, nell'ordine delle opzioni del prodotto.
   * Le opzioni nascoste (un solo valore) prendono quel valore.
   * @param {HTMLElement | null} riga
   * @returns {string[]}
   */
  #valoriRiga(riga) {
    const dati = /** @type {DatiOfferta} */ (this.#dati);
    return dati.opzioni.map((_, indice) => {
      const select = riga?.querySelector(`select[data-opzione="${indice}"]`);
      if (select instanceof HTMLSelectElement) return select.value;
      return dati.varianti[0]?.opzioni[indice] ?? '';
    });
  }

  /**
   * @param {string[]} valori
   * @returns {Variante | null}
   */
  #variantePer(valori) {
    const dati = /** @type {DatiOfferta} */ (this.#dati);
    return dati.varianti.find((variante) => variante.opzioni.every((valore, i) => valore === valori[i])) ?? null;
  }

  /**
   * @param {HTMLElement | null} riga
   * @returns {Variante | null}
   */
  #varianteRiga(riga) {
    return this.#variantePer(this.#valoriRiga(riga));
  }

  /** @returns {Variante | null} La variante del campo `id` (quella con cui la pagina è stata aperta). */
  #varianteIniziale() {
    const dati = /** @type {DatiOfferta} */ (this.#dati);
    const id = Number(this.refs.campoId?.value);
    return dati.varianti.find((variante) => variante.id === id) ?? dati.varianti[0] ?? null;
  }

  /**
   * Aggiorna testi e disponibilità delle tendine di una riga (a cascata: il colore è attivo se ha almeno
   * una taglia disponibile, le taglie seguono il colore). Le combinazioni esaurite restano visibili ma
   * non si possono scegliere; quella già scelta non viene mai spostata da sola.
   * @param {HTMLElement} riga
   */
  #aggiornaRiga(riga) {
    const dati = this.#dati;
    if (!dati) return;

    const valori = this.#valoriRiga(riga);
    for (const select of riga.querySelectorAll('select[data-opzione]')) {
      if (!(select instanceof HTMLSelectElement)) continue;
      const indice = Number(select.dataset.opzione);

      for (const opzione of select.options) {
        const candidati = dati.varianti.filter(
          (variante) =>
            variante.opzioni[indice] === opzione.value &&
            variante.opzioni.every((valore, i) => i >= indice || valore === valori[i])
        );
        let stato = '';
        if (!candidati.length) stato = dati.testi.nonDisponibile ?? '';
        else if (!candidati.some((variante) => variante.disponibile)) stato = dati.testi.esaurito ?? '';

        const testo = stato ? `${opzione.value} — ${stato}` : opzione.value;
        if (opzione.textContent !== testo) opzione.textContent = testo;
        opzione.disabled = Boolean(stato) && !opzione.selected;
      }

      this.#aggiornaVista(select);
    }
  }

  /**
   * Riporta sul riquadro disegnato il valore e il pallino del colore della tendina (che è trasparente).
   * @param {HTMLSelectElement} select
   */
  #aggiornaVista(select) {
    const campo = select.closest('.ldy-offerta__campo');
    const opzione = select.selectedOptions[0];
    if (!campo || !opzione) return;

    const valore = campo.querySelector('.ldy-offerta__valore');
    if (valore && valore.textContent !== opzione.textContent) valore.textContent = opzione.textContent;

    const chip = campo.querySelector('.ldy-offerta__chip');
    if (!(chip instanceof HTMLElement)) return;
    const immagine = opzione.dataset.chipImmagine ?? '';
    const colore = opzione.dataset.chipColore ?? '';
    chip.style.backgroundImage = immagine ? `url("${immagine}")` : '';
    chip.style.backgroundColor = colore;
    chip.hidden = !immagine && !colore;
  }

  /** Copia la riga 1 nelle righe 2 e 3 che non sono state cambiate a mano. */
  #copiaPrimaRiga() {
    const [prima, ...altre] = this.#righe();
    if (!prima) return;

    for (const riga of altre) {
      if (riga.dataset.modificata === 'true') continue;
      let cambiata = false;
      for (const select of riga.querySelectorAll('select[data-opzione]')) {
        if (!(select instanceof HTMLSelectElement)) continue;
        const sorgente = prima.querySelector(`select[data-opzione="${select.dataset.opzione}"]`);
        if (sorgente instanceof HTMLSelectElement && select.value !== sorgente.value) {
          select.value = sorgente.value;
          cambiata = true;
        }
      }
      if (cambiata) this.#aggiornaRiga(riga);
    }
  }

  /** Tiene il campo `id` del form allineato alla riga 1 (serve se il form parte senza JavaScript). */
  #sincronizzaCampoId() {
    const { campoId } = this.refs;
    if (!(campoId instanceof HTMLSelectElement)) return;
    const variante = this.#varianteRiga(this.#righe()[0] ?? null);
    if (variante) campoId.value = String(variante.id);
  }

  /** Ricalcola i prezzi delle due schede dalle righe (2+1: la somma meno il pezzo più economico). */
  async #aggiornaPrezzi() {
    const dati = this.#dati;
    if (!dati) return;

    const righe = this.#righe();
    const base = this.#varianteRiga(righe[0] ?? null) ?? this.#varianteIniziale();
    if (!base) return;

    const pacchi = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-scheda][data-quantita]')));
    if (pacchi.length) {
      await this.#aggiornaPacchi(pacchi, righe, base);
      return;
    }

    const tre = [0, 1, 2].map((i) => (righe[i] ? this.#varianteRiga(righe[i]) : null) ?? base);
    const prezzo1 = base.prezzo;
    const pieno1 = Math.max(base.pieno, base.prezzo);
    const prezzi = tre.map((variante) => variante.prezzo);
    const prezzo3 = prezzi.reduce((somma, prezzo) => somma + prezzo, 0) - Math.min(...prezzi);
    const pieno3 = tre.reduce((somma, variante) => somma + Math.max(variante.pieno, variante.prezzo), 0);

    const daCambiare = [
      [this.refs.prezzo1, prezzo1],
      [this.refs.pieno1, pieno1],
      [this.refs.prezzo3, prezzo3],
      [this.refs.pieno3, pieno3],
      [this.refs.risparmio, Math.max(0, pieno3 - prezzo3)],
    ].filter(([elemento, centesimi]) => elemento instanceof HTMLElement && Number(elemento.dataset.centesimi) !== centesimi);

    this.#scriviSconto(this.refs.sconto1, this.refs.pieno1wrap, prezzo1, pieno1, 30);
    this.#scriviSconto(this.refs.sconto3, this.refs.pieno3wrap, prezzo3, pieno3, 50);

    // Il prezzo dentro il bottone segue lo stato scelto (2+1 o 1 paio): si aggiorna prima del tocco, non dopo.
    const centesimiBottone = this.#quantita() === 3 ? prezzo3 : prezzo1;
    const { testoBottone, bottone } = this.refs;
    const conPrezzo = testoBottone.dataset.centesimi !== undefined && Boolean(testoBottone.dataset.base) && !bottone.disabled;
    const bottoneDaCambiare = conPrezzo && Number(testoBottone.dataset.centesimi) !== centesimiBottone;

    if (!daCambiare.length && !bottoneDaCambiare) return;

    try {
      this.#moduloPrezzi ??= import('@theme/money-formatting');
      const { formatMoney } = await this.#moduloPrezzi;
      for (const [elemento, centesimi] of daCambiare) {
        if (!(elemento instanceof HTMLElement) || typeof centesimi !== 'number') continue;
        elemento.textContent = formatMoney(centesimi, dati.formatoPrezzo, dati.valuta);
        elemento.dataset.centesimi = String(centesimi);
      }
      if (bottoneDaCambiare) {
        this.#testoBottone = `${testoBottone.dataset.base} – ${formatMoney(centesimiBottone, dati.formatoPrezzo, dati.valuta)}`;
        testoBottone.dataset.centesimi = String(centesimiBottone);
        if (!this.#occupato) testoBottone.textContent = this.#testoBottone;
      }
    } catch (errore) {
      console.warn('[ldy-offerta] Prezzi non aggiornati.', errore);
    }
  }

  /**
   * Modo «pacchi» (25/10/2026): per ogni scheda 2 / 3 / 4 paia ricalcola totale, barrato e prezzo al paio
   * dalle prime N righe; i pezzi in regalo (data-regalo) sono i più economici, come li toglie lo sconto
   * automatico in cassa. Il bottone e la riga «Risparmi» seguono la scheda scelta.
   * @param {HTMLElement[]} pacchi
   * @param {HTMLElement[]} righe
   * @param {Variante} base
   */
  async #aggiornaPacchi(pacchi, righe, base) {
    const dati = /** @type {DatiOfferta} */ (this.#dati);
    const scelta = this.#quantita();
    /** @type {[HTMLElement | null | undefined, number][]} */
    const valori = [];
    let centesimiBottone = base.prezzo * scelta;
    let risparmio = 0;

    for (const scheda of pacchi) {
      const quanti = Math.max(1, Number(scheda.dataset.quantita) || 1);
      const regalo = Math.min(quanti, Math.max(0, Number(scheda.dataset.regalo) || 0));
      const varianti = Array.from({ length: quanti }, (_, i) => (righe[i] ? this.#varianteRiga(righe[i]) : null) ?? base);
      const prezzi = varianti.map((variante) => variante.prezzo).sort((a, b) => a - b);
      const prezzo = prezzi.slice(regalo).reduce((somma, p) => somma + p, 0);
      const pieno = varianti.reduce((somma, variante) => somma + Math.max(variante.pieno, variante.prezzo), 0);
      // Come in Liquid (divided_by fra interi): centesimi interi, per difetto.
      const unita = Math.floor(prezzo / quanti);

      valori.push(
        [scheda.querySelector('[data-pacco-prezzo]'), prezzo],
        [scheda.querySelector('[data-pacco-pieno]'), pieno],
        [scheda.querySelector('[data-pacco-unita]'), unita]
      );
      const barrato = scheda.querySelector('[data-pacco-pienowrap]');
      if (barrato instanceof HTMLElement) barrato.hidden = !(pieno > prezzo);

      if (quanti === scelta) {
        centesimiBottone = prezzo;
        risparmio = Math.max(0, pieno - prezzo);
      }
    }

    valori.push([this.refs.risparmio, risparmio]);
    const rigaRisparmio = /** @type {HTMLElement | undefined} */ (this.refs.risparmioRiga);
    if (rigaRisparmio instanceof HTMLElement) rigaRisparmio.hidden = risparmio <= 0;

    const daCambiare = valori.filter(
      ([elemento, centesimi]) => elemento instanceof HTMLElement && Number(elemento.dataset.centesimi) !== centesimi
    );
    const { testoBottone, bottone } = this.refs;
    const conPrezzo = testoBottone.dataset.centesimi !== undefined && Boolean(testoBottone.dataset.base) && !bottone.disabled;
    const bottoneDaCambiare = conPrezzo && Number(testoBottone.dataset.centesimi) !== centesimiBottone;
    if (!daCambiare.length && !bottoneDaCambiare) return;

    try {
      this.#moduloPrezzi ??= import('@theme/money-formatting');
      const { formatMoney } = await this.#moduloPrezzi;
      for (const [elemento, centesimi] of daCambiare) {
        if (!(elemento instanceof HTMLElement)) continue;
        elemento.textContent = formatMoney(centesimi, dati.formatoPrezzo, dati.valuta);
        elemento.dataset.centesimi = String(centesimi);
      }
      if (bottoneDaCambiare) {
        this.#testoBottone = `${testoBottone.dataset.base} – ${formatMoney(centesimiBottone, dati.formatoPrezzo, dati.valuta)}`;
        testoBottone.dataset.centesimi = String(centesimiBottone);
        if (!this.#occupato) testoBottone.textContent = this.#testoBottone;
      }
    } catch (errore) {
      console.warn('[ldy-offerta] Prezzi dei pacchi non aggiornati.', errore);
    }
  }

  /**
   * @param {HTMLElement | undefined} pillola
   * @param {HTMLElement | undefined} barrato
   * @param {number} prezzo
   * @param {number} pieno
   */
  #scriviSconto(pillola, barrato, prezzo, pieno, fisso) {
    const percentuale = pieno > prezzo ? fisso : 0;
    if (barrato instanceof HTMLElement) barrato.hidden = percentuale <= 0;
    if (!(pillola instanceof HTMLElement)) return;
    pillola.hidden = percentuale <= 0;
    const testo = `-${percentuale}%`;
    if (pillola.textContent?.trim() !== testo) pillola.textContent = testo;
  }

  /**
   * Cambia la foto principale quando la riga 1 passa a un colore con un'altra foto, con l'evento
   * standard di selezione prodotto: la galleria del tema si sostituisce con quella renderizzata
   * per la variante (stesso meccanismo del selettore varianti di Horizon).
   */
  #aggiornaGalleria() {
    const dati = this.#dati;
    const idSezione = this.dataset.sectionId;
    const prima = this.#righe()[0];
    if (!dati || !idSezione || !prima) return;

    const valori = this.#valoriRiga(prima);
    const esatta = this.#variantePer(valori);
    const selectColore = prima.querySelector('.ldy-offerta__campo--colore select[data-opzione]');
    const indiceColore = selectColore instanceof HTMLSelectElement ? Number(selectColore.dataset.opzione) : -1;

    let obiettivo = esatta?.media ? esatta : null;
    if (!obiettivo && indiceColore >= 0) {
      obiettivo =
        dati.varianti.find((variante) => variante.media && variante.opzioni[indiceColore] === valori[indiceColore]) ??
        null;
    }
    if (!obiettivo || obiettivo.media === this.#mediaMostrata) return;

    const scelta = obiettivo;
    this.#mediaMostrata = scelta.media;
    this.#richiestaGalleria?.abort();
    const controller = new AbortController();
    this.#richiestaGalleria = controller;

    const differito = ProductSelectEvent.createPromise();
    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: String(dati.prodotto.id),
          title: dati.prodotto.titolo,
          handle: dati.prodotto.handle,
        },
        selectedOptions: dati.opzioni.map((nome, i) => ({ name: nome, value: scelta.opzioni[i] ?? '' })),
        detail: { variantId: String(scelta.id) },
        promise: differito.promise,
      })
    );

    const url = new URL(dati.prodotto.url, window.location.origin);
    url.searchParams.set('variant', String(scelta.id));
    url.searchParams.set('section_id', idSezione);

    fetch(url, { signal: controller.signal })
      .then((risposta) => {
        if (!risposta.ok) throw new Error(`HTTP ${risposta.status}`);
        return risposta.text();
      })
      .then((testo) => {
        const html = new DOMParser().parseFromString(testo, 'text/html');
        let variante = null;
        try {
          const payload = html.querySelector('product-component[view-event-payload]')?.getAttribute('view-event-payload');
          variante = payload ? JSON.parse(payload)?.product?.selectedVariant ?? null : null;
        } catch {
          variante = null;
        }

        if (this.#richiestaGalleria === controller) this.#richiestaGalleria = null;
        differito.resolve({
          variant: variante,
          detail: {
            html,
            productId: String(dati.prodotto.id),
            sourceId: String(scelta.id),
            resource: { id: scelta.id, sku: scelta.sku, available: scelta.disponibile },
          },
        });
      })
      .catch((errore) => {
        if (this.#richiestaGalleria === controller) this.#richiestaGalleria = null;
        if (errore?.name !== 'AbortError') {
          this.#mediaMostrata = null;
          console.warn('[ldy-offerta] Foto non aggiornata.', errore);
        }
        differito.reject(errore);
      });
  }

  /**
   * Controlla le righe attive e somma le varianti uguali. Se una riga non si può comprare
   * mostra il messaggio sulla riga e restituisce null.
   * @returns {Map<number, number> | null}
   */
  #pezziDaAggiungere() {
    const dati = /** @type {DatiOfferta} */ (this.#dati);
    const quantita = this.#quantita();
    const righe = this.#righe().slice(0, quantita);
    /** @type {Map<number, number>} */
    const pezzi = new Map();

    if (!righe.length) {
      const variante = this.#varianteIniziale();
      if (!variante?.disponibile) {
        this.#mostraErrore(dati.testi.esaurito ?? '');
        return null;
      }
      pezzi.set(variante.id, quantita);
      return pezzi;
    }

    for (const riga of righe) {
      const variante = this.#varianteRiga(riga);
      if (!variante || !variante.disponibile) {
        const modello = variante ? dati.testi.esauritoRiga : dati.testi.nonDisponibileRiga;
        this.#segnalaRiga(riga, (modello ?? '').replace('__N__', riga.dataset.riga ?? ''));
        return null;
      }
      pezzi.set(variante.id, (pezzi.get(variante.id) ?? 0) + 1);
    }

    return pezzi;
  }

  /**
   * @param {HTMLElement} riga
   * @param {string} messaggio
   */
  #segnalaRiga(riga, messaggio) {
    const idErrore = this.refs.errore.id;
    const tendine = Array.from(riga.querySelectorAll('select[data-opzione]'));
    for (const select of tendine) {
      select.setAttribute('aria-invalid', 'true');
      if (idErrore) select.setAttribute('aria-describedby', idErrore);
    }
    this.#mostraErrore(messaggio);
    const prima = tendine[0];
    if (prima instanceof HTMLSelectElement) prima.focus();
  }

  /** @param {string} messaggio */
  #mostraErrore(messaggio) {
    const { errore } = this.refs;
    errore.textContent = '';
    cancelAnimationFrame(this.#fotogrammaErrore);
    // Il testo arriva nel fotogramma dopo, così la zona aria-live lo legge anche se è uguale al precedente.
    this.#fotogrammaErrore = requestAnimationFrame(() => {
      errore.textContent = messaggio;
    });
  }

  #pulisciErrore() {
    cancelAnimationFrame(this.#fotogrammaErrore);
    this.refs.errore.textContent = '';
    for (const select of this.querySelectorAll('select[aria-invalid]')) {
      select.removeAttribute('aria-invalid');
      select.removeAttribute('aria-describedby');
    }
  }

  /** @param {boolean} occupato */
  #impostaOccupato(occupato) {
    this.#occupato = occupato;
    const { bottone, testoBottone } = this.refs;
    if (occupato) {
      bottone.setAttribute('aria-busy', 'true');
      testoBottone.textContent = this.#dati?.testi.inAggiunta ?? this.#testoBottone;
    } else {
      bottone.removeAttribute('aria-busy');
      testoBottone.textContent = this.#testoBottone;
    }
  }

  /**
   * @param {unknown} errore
   * @param {boolean} scaduto - true se la richiesta è stata fermata dopo 12 secondi.
   */
  #gestisciErrore(errore, scaduto) {
    const testi = this.#dati?.testi ?? {};
    let messaggio = testi.erroreGenerico ?? '';
    /** @type {'INVALID' | 'MAXIMUM_EXCEEDED' | 'SERVICE_UNAVAILABLE'} */
    let codice = 'INVALID';

    if (scaduto) {
      messaggio = testi.erroreTempo ?? messaggio;
      codice = 'SERVICE_UNAVAILABLE';
    } else if (errore instanceof ErroreCarrello && errore.tipo === 'disponibilita') {
      messaggio = errore.dettaglio || testi.erroreDisponibilita || messaggio;
      codice = 'MAXIMUM_EXCEEDED';
    } else if (!navigator.onLine) {
      messaggio = testi.erroreRete ?? messaggio;
      codice = 'SERVICE_UNAVAILABLE';
    } else if (!(errore instanceof ErroreCarrello)) {
      console.warn('[ldy-offerta] Aggiunta al carrello non riuscita.', errore);
    }

    this.#impostaOccupato(false);
    this.#mostraErrore(messaggio);
    this.dispatchEvent(new CartErrorEvent({ error: messaggio, code: codice, detail: { source: 'ldy-offerta' } }));

    // Dopo un 422 o un'attesa troppo lunga qualcosa può essere entrato lo stesso: si riallinea il carrello.
    if (navigator.onLine) this.#riallineaCarrello();
  }

  /** @returns {string[]} Le sezioni con le righe del carrello presenti nella pagina (massimo 5). */
  #sezioniCarrello() {
    /** @type {Set<string>} */
    const id = new Set();
    for (const elemento of document.querySelectorAll('cart-items-component[data-section-id]')) {
      if (elemento instanceof HTMLElement && elemento.dataset.sectionId) id.add(elemento.dataset.sectionId);
    }
    return [...id].slice(0, 5);
  }

  /**
   * Legge il carrello dal `view-event-payload` delle righe del cassetto renderizzate da Shopify.
   * @param {string | null | undefined} html
   */
  #carrelloDaHtml(html) {
    if (typeof html !== 'string') return null;
    try {
      const documento = new DOMParser().parseFromString(html, 'text/html');
      const payload = documento.querySelector('cart-items-component[view-event-payload]')?.getAttribute('view-event-payload');
      const carrello = payload ? JSON.parse(payload)?.cart : null;
      if (carrello && Array.isArray(carrello.lines) && typeof carrello.totalQuantity === 'number') return carrello;
    } catch {
      // Payload non leggibile: si ripiega su /cart.js.
    }
    return null;
  }

  /** Solo se le sezioni non hanno portato il carrello: una lettura di /cart.js, anche lei con tempo massimo. */
  async #leggiCarrello() {
    const dati = this.#dati;
    if (!dati) return null;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TEMPO_MASSIMO);
    try {
      const risposta = await fetch(dati.urlCarrelloJs, { headers: { Accept: 'application/json' }, signal: controller.signal });
      if (!risposta.ok) return null;
      return CartLinesUpdateEvent.createCartFromAjaxResponse(await risposta.json());
    } catch {
      return null;
    } finally {
      clearTimeout(timer);
    }
  }

  /**
   * Aggiorna il cassetto prima di aprirlo, così si apre già con le righe giuste e le foto pronte.
   * @param {string | null | undefined} html
   */
  async #riempiCassetto(html) {
    if (typeof html !== 'string') return;
    if (!document.getElementById(`shopify-section-${SEZIONE_CASSETTO}`)) return;

    await this.#preparaFoto(html);

    const sezione = document.getElementById(`shopify-section-${SEZIONE_CASSETTO}`);
    if (!sezione) return;
    const eraVuoto = sezione.querySelector('[data-cart-drawer-empty]') !== null;

    try {
      sectionRenderer.abortRender(SEZIONE_CASSETTO);
      await morphSection(SEZIONE_CASSETTO, html, { mode: 'hydration', injectStylesheet: eraVuoto });
    } catch (errore) {
      console.warn('[ldy-offerta] Cassetto non aggiornato.', errore);
    }
  }

  /**
   * Scarica e decodifica le foto delle righe del cassetto, aspettando al massimo ATTESA_FOTO.
   * Solo le foto delle righe (classe di Horizon): i consigliati sono lazy e non devono ritardare l'apertura.
   * @param {string} html
   */
  async #preparaFoto(html) {
    const documento = new DOMParser().parseFromString(html, 'text/html');
    const immagini = Array.from(documento.querySelectorAll('cart-items-component img.cart-items__media-image')).slice(0, 12);
    if (!immagini.length) return;

    const attese = immagini.map((img) => {
      const foto = new Image();
      const sizes = img.getAttribute('sizes');
      const srcset = img.getAttribute('srcset');
      if (sizes && sizes !== 'auto') foto.sizes = sizes;
      if (srcset) foto.srcset = srcset;
      foto.src = img.getAttribute('src') ?? '';
      return foto.decode().catch(() => undefined);
    });

    await Promise.race([Promise.all(attese), new Promise((risolvi) => setTimeout(risolvi, ATTESA_FOTO))]);
  }

  /** Apre il cassetto del tema; se il tema usa la pagina carrello, ci va. */
  #apriCassetto() {
    const cassetto = document.querySelector('theme-drawer#cart-drawer');
    if (!(cassetto instanceof HTMLElement)) {
      window.location.assign(this.#dati?.urlCarrello ?? '/cart');
      return;
    }

    const apri = () => {
      const drawer = /** @type {HTMLElement & { open?: () => void, isOpen?: boolean }} */ (cassetto);
      if (typeof drawer.open === 'function' && !drawer.isOpen) drawer.open();
    };

    if (customElements.get('theme-drawer')) apri();
    else customElements.whenDefined('theme-drawer').then(apri);
  }

  /** Dopo un errore: una lettura delle sezioni del carrello per allineare cassetto e numerino. */
  async #riallineaCarrello() {
    const sezioni = this.#sezioniCarrello();
    if (!sezioni.length) return;

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TEMPO_MASSIMO);
    try {
      const url = new URL(window.location.pathname, window.location.origin);
      url.searchParams.set('sections', sezioni.join(','));
      const risposta = await fetch(url, { signal: controller.signal });
      if (!risposta.ok) return;
      const json = await risposta.json();
      const html = json?.[SEZIONE_CASSETTO];
      const carrello = this.#carrelloDaHtml(html);
      await this.#riempiCassetto(html);

      if (carrello) {
        for (const icona of document.querySelectorAll('cart-icon')) {
          const elemento = /** @type {HTMLElement & { renderCartBubble?: (n: number, animate?: boolean) => void }} */ (
            icona
          );
          if (typeof elemento.renderCartBubble === 'function') elemento.renderCartBubble(carrello.totalQuantity, false);
        }
      }
    } catch (errore) {
      if (errore instanceof Error && errore.name !== 'AbortError') console.warn('[ldy-offerta] Carrello non riallineato.', errore);
    } finally {
      clearTimeout(timer);
    }
  }

  /** Scrive «Ordina ora e ricevila tra [da] e [a]» con le date dei giorni lavorativi. */
  #mostraConsegna() {
    const { consegna, consegnaTesto } = this.refs;
    const impostazioni = this.#dati?.consegna;
    if (!(consegna instanceof HTMLElement) || !(consegnaTesto instanceof HTMLElement) || !impostazioni?.testo) return;

    const minimo = Math.max(1, Number(impostazioni.min) || 5);
    const massimo = Math.max(minimo, Number(impostazioni.max) || minimo);
    const oggi = new Date();
    const date = { '[da]': aggiungiGiorniLavorativi(oggi, minimo), '[a]': aggiungiGiorniLavorativi(oggi, massimo) };

    let formato;
    try {
      formato = new Intl.DateTimeFormat(document.documentElement.lang || 'it', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
      });
    } catch {
      formato = new Intl.DateTimeFormat('it', { weekday: 'short', day: 'numeric', month: 'short' });
    }

    const frammento = document.createDocumentFragment();
    for (const parte of impostazioni.testo.split(/(\[da\]|\[a\])/g)) {
      if (parte === '[da]' || parte === '[a]') {
        const data = document.createElement('strong');
        data.textContent = formato.format(date[parte]);
        frammento.append(data);
      } else if (parte) {
        frammento.append(parte);
      }
    }

    consegnaTesto.replaceChildren(frammento);
    consegna.hidden = false;
  }
}

/**
 * Trasforma le entità HTML in caratteri («&euro;» → «€») senza eseguire niente (textarea = solo testo).
 * @param {string} testo
 * @returns {string}
 */
function decodificaEntita(testo) {
  if (!testo.includes('&')) return testo;
  const area = document.createElement('textarea');
  area.innerHTML = testo;
  return area.value;
}

/** @type {Map<number, Set<string>>} */
const festivita = new Map();

/**
 * @param {Date} data
 * @returns {string} AAAA-MM-GG
 */
function chiaveData(data) {
  const mese = String(data.getMonth() + 1).padStart(2, '0');
  const giorno = String(data.getDate()).padStart(2, '0');
  return `${data.getFullYear()}-${mese}-${giorno}`;
}

/**
 * Domenica di Pasqua (calendario gregoriano).
 * @param {number} anno
 */
function pasqua(anno) {
  const a = anno % 19;
  const b = Math.floor(anno / 100);
  const c = anno % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const mese = Math.floor((h + l - 7 * m + 114) / 31);
  const giorno = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(anno, mese - 1, giorno);
}

/**
 * Feste nazionali italiane (i corrieri non consegnano).
 * @param {number} anno
 */
function festivitaDellAnno(anno) {
  let elenco = festivita.get(anno);
  if (elenco) return elenco;

  const fisse = ['01-01', '01-06', '04-25', '05-01', '06-02', '08-15', '11-01', '12-08', '12-25', '12-26'];
  // San Francesco (4 ottobre) è di nuovo festa nazionale dal 2026.
  if (anno >= 2026) fisse.push('10-04');

  const pasquetta = pasqua(anno);
  pasquetta.setDate(pasquetta.getDate() + 1);

  elenco = new Set([...fisse.map((giorno) => `${anno}-${giorno}`), chiaveData(pasquetta)]);
  festivita.set(anno, elenco);
  return elenco;
}

/**
 * @param {Date} partenza
 * @param {number} giorni - Giorni lavorativi da aggiungere (sabato, domenica e feste esclusi).
 */
function aggiungiGiorniLavorativi(partenza, giorni) {
  const data = new Date(partenza.getFullYear(), partenza.getMonth(), partenza.getDate());
  let contati = 0;
  while (contati < giorni) {
    data.setDate(data.getDate() + 1);
    const giornoSettimana = data.getDay();
    if (giornoSettimana === 0 || giornoSettimana === 6) continue;
    if (festivitaDellAnno(data.getFullYear()).has(chiaveData(data))) continue;
    contati += 1;
  }
  return data;
}

if (!customElements.get('ldy-offerta-component')) {
  customElements.define('ldy-offerta-component', LdyOffertaComponent);
}
